import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import PropTypes from "prop-types";

import { config, domUtils } from "../../helpers/payeezy";
import useScript from "../../hooks/useScript";
import PayeezyInput from "../UI/PayeezyInput";
import "./PaymentForm.css";
import { uiActions } from "../../store/ui/slice";
import TermsAndConditions from "./TermsAndConditions";
import { Box, Tooltip } from "@mui/material";
import { Typography } from "@mui/material";
import InfoIcon from "@mui/icons-material/Info";
import Loader from "../UI/Loader";
import { Button } from "@mui/material";

const INITIAL_REG_SUBMIT_CONFIG = {
  disabled: true,
  text: "Register & Pay",
};

const INITIAL_NOREG_SUBMIT_CONFIG = {
  disabled: true,
  text: "Pay Now",
};

// payeezy form for collecting credit card info
const PaymentForm = ({
                       isRegistering = false,
                       totalPrice,
                       onSubmit,
                       onSubmitMsg,
                       totalDiscount = 0,
                     }) => {
  const SUBMIT_CONFIG = isRegistering
      ? INITIAL_REG_SUBMIT_CONFIG
      : INITIAL_NOREG_SUBMIT_CONFIG;
  const paymentjsv2 = useScript(
      `https://lib.paymentjs.firstdata.com/${import.meta.env.VITE_PAYEEZY_ENV}/client-${import.meta.env.VITE_PAYEEZY_VERSION}.js`
  );
  const dispatch = useDispatch();
  const [isFormCreated, setIsFormCreated] = useState(false);
  const [isFormLoaded, setIsFormLoaded] = useState(false);
  const [submitConfig, setSubmitConfig] = useState(SUBMIT_CONFIG);
  const firstData = window.firstdata;
  const USDollar = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  });

  // Get the registrations and discount information from the Redux store
  const { registrations, discountCode, hasClemsonCoupon, immediateDiscountAmount } = useSelector((state) => state.cart);

  // Calculate the final price after applying appropriate discounts
  const calculateFinalPrice = () => {
    // Use the immediate discount amount that was calculated in OrderSummary
    // This ensures consistency between the two displays
    return Math.max(0, totalPrice - (immediateDiscountAmount || 0));
  };

  // Helper for tooltips
  const InfoTooltip = ({ title }) => (
      <Tooltip title={title} placement="bottom">
        <InfoIcon fontSize="small" sx={{ marginLeft: 1, verticalAlign: 'middle', fontSize: '16px', color: 'gray' }} />
      </Tooltip>
  );

  const showTotalPrice = () => {
    if (totalDiscount > 0) {
      if (hasClemsonCoupon) {
        return (
            <Box>
              <Typography align="center" variant="body2" color="green">
                Discount (applied now): -{USDollar.format(immediateDiscountAmount)}
                <InfoTooltip title="This is the portion of your discount that applies to sessions being paid in full. Any remaining discount will be applied to your future balance for sessions where you're paying just the deposit." />
              </Typography>
              <Typography align="center" fontWeight={700}>
                Total charged to card: {USDollar.format(calculateFinalPrice())}
              </Typography>
            </Box>
        );
      } else {
        // Regular discount coupon (non-Clemson)
        return (
            <Box>
              <Typography align="center" variant="body2" color="green">
                Discount ({discountCode}): -{USDollar.format(immediateDiscountAmount)}
              </Typography>
              <Typography align="center" fontWeight={700}>
                Total charged to card: {USDollar.format(calculateFinalPrice())}
              </Typography>
            </Box>
        );
      }
    } else {
      return (
          <Typography align="center" fontWeight={700}>
            Total charged to card: {USDollar.format(totalPrice)}
          </Typography>
      );
    }
  };

  useEffect(() => {
    let data = {}; // stores client token and nonce

    // initialize payeezy form after the library has loaded
    if (paymentjsv2 === "ready" && !isFormCreated) {
      const submitButton = {
        enable: () => {
          setSubmitConfig({ ...submitConfig, disabled: false });
        },

        setSubmitState: () => {
          setSubmitConfig({
            ...submitConfig,
            disabled: true,
            text: "Processing...",
          });
        },

        removeSubmitState: () => {
          setSubmitConfig({
            ...submitConfig,
            disabled: false,
            text: SUBMIT_CONFIG.text,
          });
        },
      };

      // sends payeezy api request for client token and nonce
      const authorizeSession = (callback) => {
        dispatch(uiActions.setAlertInfo(onSubmitMsg));
        let request = new XMLHttpRequest();
        request.onload = () => {
          if (request.status >= 200 && request.status < 300) {
            const response = JSON.parse(request.responseText);
            const { clientToken, nonce } = response;
            data = { clientToken, nonce };
            callback(response);
          } else {
            dispatch(
                uiActions.setAlertError(
                    "Failed to process payment. Please try another time."
                )
            );
            submitButton.removeSubmitState();
          }
          request = null;
        };

        request.open(
            "GET",
            import.meta.env.VITE_API_URL + "authorize_session",
            true
        );
        request.send();
      };

      const payeezyHooks = {
        preFlowHook: authorizeSession,
      };

      // create event handlers for payeezy form error and success cases
      const onCreate = (paymentForm) => {
        const onSuccess = () => {
          submitButton.removeSubmitState();
          onSubmit(data);
        };

        const onError = (error) => {
          let errorMessage =
              "Failed to process payment. Please try another time.";
          if (error.message === "form validation failed") {
            errorMessage = "Please fill out all fields.";
          }
          dispatch(uiActions.setAlertError(errorMessage));

          submitButton.removeSubmitState();
        };

        const form = domUtils.getEl("#payment-form");
        form.addEventListener("submit", (e) => {
          e.preventDefault();
          submitButton.setSubmitState();
          paymentForm.onSubmit(onSuccess, onError);
        });

        const ccFields =
            window.document.getElementsByClassName("payment-fields");
        for (let i = 0; i < ccFields.length; i++) {
          domUtils.removeClass(ccFields[i], "disabled");
        }

        setIsFormLoaded(true);
        submitButton.enable();
      };

      // create payeezy form using above config
      if (firstData) {
        const logger = (level, msg) => console.log(msg);
        firstData.createPaymentForm(config, payeezyHooks, onCreate, logger);
        setIsFormCreated(true);
      }
    }

    return () => {
      if (window.firstData) {
        delete window.firstdata;
        setIsFormLoaded(false);
        setIsFormCreated(false);
      }
    };
  }, [paymentjsv2]);

  return (
      <Box
          component="form"
          id="payment-form"
          justifyContent="center"
          alignItems="center"
      >
        {/* Hide payeezy form until api responds with iframe inputs */}
        <Box sx={{ display: isFormLoaded ? "block" : "none" }}>
          <PayeezyInput label="Cardholder Name" id="cc-name" data-cc-name />
          <PayeezyInput label="Card Number" id="cc-card" data-cc-card />
          <PayeezyInput label="Expiration Date" id="cc-exp" data-cc-exp />
          <PayeezyInput label="CVV" id="cc-cvv" data-cc-cvv />
        </Box>
        {/* Show loader until payeezy is done loading */}
        <Box
            justifyContent="center"
            sx={{ display: isFormLoaded ? "none" : "flex" }}
        >
          <Loader />
        </Box>
        <TermsAndConditions isRegistering={isRegistering} />
        <Box p={1}>{showTotalPrice()}</Box>
        <Button
            type="submit"
            variant="contained"
            id="submit-button"
            disabled={submitConfig.disabled}
            data-submit-btn
            fullWidth
        >
          {submitConfig.text}
        </Button>
        <input type="hidden" name="clientToken" id="clientToken" value="" />
        <input type="hidden" name="nonce" id="nonce" value="" />
      </Box>
  );
};

PaymentForm.propTypes = {
  totalPrice: PropTypes.number.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default PaymentForm;